import { NotificationType } from '~/app/enums/NotificationId';
import { PairingStatus } from '~/app/enums/PairingStatus';
import { TherapyJobStatus } from '~/app/enums/TherapyJobStatus';
import { SystemNotification } from '~/app/models/Notification';
import { TherapyJobV2 } from '~/app/models/TherapyJobV2';
import { isServerNotification } from './NotificationFunctions';

/** Device disconnected is shown only for jobs in these statuses (GRAVPL-2489). */
export const DEVICE_DISCONNECTED_JOB_STATUSES: ReadonlyArray<TherapyJobStatus> = [
  TherapyJobStatus.PRESCRIBED,
  TherapyJobStatus.RUNNING
];

/**
 * DEVICE_DISCONNECTED, the one notification-center type the web UI derives (ADR 0002, decision 5): the job is
 * PRESCRIBED or RUNNING, its device is paired and offline. Not hidden by abortion triggered.
 * Kept up to date by `/list` and the liveness `DEVICE_ERROR_STATE` events (`deviceDisconnected`, `disconnectedSince`).
 */
export const deviceDisconnectedNotification = (job: Partial<TherapyJobV2>): SystemNotification | null => {
  const shown =
    DEVICE_DISCONNECTED_JOB_STATUSES.includes(job.status) &&
    job.pairingState === PairingStatus.PAIRED &&
    job.deviceDisconnected === true;

  return shown
    ? { therapyJobId: job.id, type: 'DEVICE_DISCONNECTED', severity: 'WARNING', since: job.disconnectedSince ?? null }
    : null;
};

const isDeviceDisconnected = (type: keyof typeof NotificationType): boolean =>
  NotificationType[type] === NotificationType.DEVICE_DISCONNECTED;

/**
 * The server notifications the web UI renders for a job: its `notifications` (the server's complete set, replaced
 * by every per-job WebSocket event) of the types the web UI renders from the server, plus the derived
 * DEVICE_DISCONNECTED.
 */
export const notificationsOfJob = (job: Partial<TherapyJobV2> | undefined): Array<SystemNotification> => {
  if (!job) {
    return [];
  }

  const fromServer: Array<SystemNotification> = (job.notifications ?? [])
    .filter((n) => isServerNotification(NotificationType[n.type]) && !isDeviceDisconnected(n.type))
    .map((n) => ({ therapyJobId: job.id, ...n, since: n.since ?? null }));
  const disconnected = deviceDisconnectedNotification(job);

  return disconnected ? [...fromServer, disconnected] : fromServer;
};
